import type React from "react";
import type { AnchorHTMLAttributes } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { remarkEmojiShortcodeCompat } from "@/lib/remark/remarkEmojiShortcodeCompat";
import { remarkNormalizeListItemSoftBreaks } from "@/lib/remark/remarkNormalizeListItemSoftBreaks";
import { remarkPreserveEmptyLines } from "@/lib/remark/remarkPreserveEmptyLines";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { isSafeUrl } from "@/lib/utils/urlUtils";

export interface LinkRendererArgs {
  href?: string;
  children: React.ReactNode;
  props: AnchorHTMLAttributes<HTMLAnchorElement>;
}

export interface MarkdownRendererProps {
  content: string;
  renderLink?: (args: LinkRendererArgs) => React.ReactNode | undefined;
  enableEmojiShortcodeCompat?: boolean;
}

const ALLOWED_ELEMENTS = [
  "p",
  "a",
  "img",
  "code",
  "pre",
  "strong",
  "em",
  "del",
  "sup",
  "sub",
  "kbd",
  "ul",
  "ol",
  "li",
  "blockquote",
  "hr",
  "br",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
];

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type HeadingComponent = React.FC<React.HTMLAttributes<HTMLHeadingElement>>;

const createHeadingComponent = (Tag: HeadingTag) => {
  const Component: HeadingComponent = ({ children, ...props }) => (
    <Tag {...props}>{children}</Tag>
  );
  return Component;
};

const HEADING_COMPONENTS: Record<HeadingTag, HeadingComponent> = {
  h1: createHeadingComponent("h1"),
  h2: createHeadingComponent("h2"),
  h3: createHeadingComponent("h3"),
  h4: createHeadingComponent("h4"),
  h5: createHeadingComponent("h5"),
  h6: createHeadingComponent("h6"),
};

const CODE_INLINE_STYLE: React.CSSProperties = {
  backgroundColor: "var(--rw-warm-gray)",
  padding: "2px 4px",
  borderRadius: "3px",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const PRE_BLOCK_STYLE: React.CSSProperties = {
  backgroundColor: "var(--rw-warm-gray)",
  padding: "1rem",
  borderRadius: "4px",
  maxWidth: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  whiteSpace: "pre",
};

// Separate component to handle image loading state
const ImageRenderer: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = ({
  src,
  alt,
  ...props
}) => {
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation();

  if (hasError) {
    return (
      <span className="italic text-gray-400">{t("common.invalidImage")}</span>
    );
  }

  return (
    <img
      src={src}
      alt={alt || ""}
      {...props}
      className="h-auto max-w-full rounded-md object-contain"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setHasError(true)}
    />
  );
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  renderLink,
  enableEmojiShortcodeCompat = false,
}) => {
  const { t } = useTranslation();

  const sanitizedContent = useMemo(
    () =>
      content.replace(
        /~{3,}/g,
        (match) => match.replace(/~/g, "\\~"), // 3つ以上の~はMarkdownのフェンスとして解釈させず、そのまま表示する
      ),
    [content],
  );

  const remarkPlugins = useMemo(
    () =>
      enableEmojiShortcodeCompat
        ? [
            remarkGfm,
            remarkNormalizeListItemSoftBreaks,
            remarkBreaks,
            remarkPreserveEmptyLines,
            remarkEmojiShortcodeCompat,
          ]
        : [
            remarkGfm,
            remarkNormalizeListItemSoftBreaks,
            remarkBreaks,
            remarkPreserveEmptyLines,
          ],
    [enableEmojiShortcodeCompat],
  );

  if (!content.trim()) return null;

  return (
    <div className="markdown-content min-w-0 max-w-full">
      <TooltipProvider delayDuration={150}>
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          // 想定外のタグは出力しない
          allowedElements={ALLOWED_ELEMENTS}
          unwrapDisallowed
          components={{
            img: ({ src, alt, ...props }) => {
              if (!src || !isSafeUrl(src)) {
                return (
                  <span className="italic text-gray-400">
                    {t("common.invalidImage")}
                  </span>
                );
              }

              return <ImageRenderer src={src} alt={alt || ""} {...props} />;
            },
            a: ({ href, children, ...props }) => {
              const custom = renderLink?.({ href, children, props });
              if (custom !== undefined) {
                return custom;
              }
              if (!href || href.startsWith("file-guid:") || !isSafeUrl(href)) {
                return <span className="text-gray-500">{children}</span>;
              }
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={href}
                      {...props}
                      className="break-all text-blue-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                    >
                      {children}
                    </a>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs break-all text-xs">
                    {href}
                  </TooltipContent>
                </Tooltip>
              );
            },
            p: ({ children, ...props }) => {
              const mergedStyle: React.CSSProperties = {
                wordBreak: "break-word",
                overflowWrap: "break-word",
                ...(props.style ?? {}),
              };
              return (
                <p {...props} style={mergedStyle}>
                  {children}
                </p>
              );
            },
            h1: HEADING_COMPONENTS.h1,
            h2: HEADING_COMPONENTS.h2,
            h3: HEADING_COMPONENTS.h3,
            h4: HEADING_COMPONENTS.h4,
            h5: HEADING_COMPONENTS.h5,
            h6: HEADING_COMPONENTS.h6,
            code: ({ children, className, ...props }) => (
              <code
                className={className}
                {...props}
                style={{ ...CODE_INLINE_STYLE, ...(props.style ?? {}) }}
              >
                {children}
              </code>
            ),
            pre: ({ children, ...props }) => (
              <pre
                {...props}
                style={{ ...PRE_BLOCK_STYLE, ...(props.style ?? {}) }}
              >
                {children}
              </pre>
            ),
          }}
        >
          {sanitizedContent}
        </ReactMarkdown>
      </TooltipProvider>
    </div>
  );
};
